/**
 * In-house ("preferred") hotel pricing engine.
 *
 * Prices ONE room for ONE night from a resolved rate card + the room's
 * occupancy. Callers loop nights (each night with its own date-correct rate
 * card) and sum rooms. Pure + currency-agnostic: amounts are in the rate card's
 * own units (e.g. INR/USD, NOT paise) - the caller converts to minor units.
 *
 * Two pricing methods, chosen automatically:
 *   - FIXED    (`fixedRoomCost > 0`): the room has one whole-room price that
 *     covers a base occupancy (base adults + a free-child allowance); people
 *     beyond base add supplements.
 *   - PER_PAX  (`fixedRoomCost == 0`): every person is priced individually
 *     (adult / extra-adult / child-with-bed / child-without-bed / infant).
 *
 * Occupancy is the gate: a room that can't physically hold the configured pax
 * (bed-needing occupants over `maxOccupancy`) returns `unavailableReason`
 * instead of a price ("skip-and-flag"), so we never quote an impossible room.
 * Capacity is fully expressed by two numbers - base occupancy (what the base
 * rate covers) and max occupancy (base + any extra beds the room allows); a
 * strict room simply has `max == base`. Children sharing existing bedding
 * (no bed) and infants don't consume a bed slot, so they sit outside the cap.
 *
 * Age bands are fixed by policy (child = 2-11.99y, infant = 0-1.99y) and are
 * already captured as counts on the itinerary room - the engine takes counts,
 * not ages.
 */

export interface HotelRateInput {
  /** Whole-room price. `> 0` selects FIXED mode; `0` selects PER_PAX. */
  fixedRoomCost: number;
  /** Per-adult price (PER_PAX only), on the base sharing basis. */
  adultPrice: number;
  /** Per-child price when the child occupies a bed. */
  childWithBedPrice: number;
  /** Per-child price when the child shares existing bedding (no bed). */
  childWithoutBedPrice: number;
  /** Per-infant price (usually 0 - infants are typically free). */
  infantPrice: number;
  /** Supplement per adult beyond base occupancy (bed included, by design). */
  extraAdultPrice: number;
  /** Meal supplement per adult per night (0 when the rate already includes meals). */
  mealPerAdult: number;
  /** Meal supplement per child per night. */
  mealPerChild: number;
  /** Adults the base rate covers (rate-card base occupancy; default 2 = double). */
  baseAdults: number;
  /** Children included free in the base (FIXED mode); typically 1. */
  baseChildrenIncluded: number;
  /**
   * Hard capacity of the room in bed-needing occupants (adults +
   * children-with-bed). `max == baseAdults` means a strict room with no extra
   * bed; `max > baseAdults` means `max - baseAdults` extra beds are allowed.
   */
  maxOccupancy: number;
}

export interface RoomOccupancy {
  adults: number;
  /** Children aged 2-11.99. */
  children: number;
  /** How many of `children` share existing bedding (priced without a bed). */
  childrenNoBed: number;
  /** Infants aged 0-1.99. */
  infants: number;
}

export interface RoomNightPriceLine {
  label: string;
  qty: number;
  unit: number;
  amount: number;
}

export interface RoomNightPrice {
  /** Total for one room for one night, in the rate card's currency units. */
  amount: number;
  /** Non-null when the room can't hold this occupancy - price is 0/ignored. */
  unavailableReason: string | null;
  /** Itemised breakdown (admin-only display). */
  lines: RoomNightPriceLine[];
  /** 'FIXED' | 'PER_PAX' - which method was applied. */
  mode: 'FIXED' | 'PER_PAX';
}

const clampNonNeg = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);

/** Price one room for one night. Amounts are in the rate card's currency units. */
export function priceRoomNight(rate: HotelRateInput, occ: RoomOccupancy): RoomNightPrice {
  const adults = Math.max(0, Math.floor(occ.adults));
  const children = Math.max(0, Math.floor(occ.children));
  const infants = Math.max(0, Math.floor(occ.infants));
  const childrenNoBed = Math.min(Math.max(0, Math.floor(occ.childrenNoBed)), children);

  const baseAdults = rate.baseAdults > 0 ? rate.baseAdults : 2;
  const baseChildren = Math.max(0, rate.baseChildrenIncluded);
  const mode: 'FIXED' | 'PER_PAX' = rate.fixedRoomCost > 0 ? 'FIXED' : 'PER_PAX';

  const childrenWithBed = children - childrenNoBed;

  // ---- Availability gate (skip-and-flag) ----
  // Only bed-needing people count against capacity: adults + children who take
  // a bed. Children sharing existing bedding (no bed) and infants sit outside
  // the cap. Capacity is the room's max occupancy; base occupancy always fits
  // (guard against a max mis-keyed below the base). A strict room has
  // max == base (no extra bed); a room that allows one rollaway has max = base+1.
  const bedOccupants = adults + childrenWithBed;
  const capacity = Math.max(rate.maxOccupancy, baseAdults);
  if (capacity > 0 && bedOccupants > capacity) {
    return {
      amount: 0,
      unavailableReason: `Exceeds max occupancy (${bedOccupants} needing a bed vs ${capacity})`,
      lines: [],
      mode,
    };
  }

  const lines: RoomNightPriceLine[] = [];
  const add = (label: string, qty: number, unit: number): void => {
    const u = clampNonNeg(unit);
    if (qty > 0 && u > 0) lines.push({ label, qty, unit: u, amount: qty * u });
  };

  if (mode === 'FIXED') {
    const fixed = clampNonNeg(rate.fixedRoomCost);
    lines.push({ label: 'Room (base occupancy)', qty: 1, unit: fixed, amount: fixed });
    const extraAdults = Math.max(0, adults - baseAdults);
    add('Extra adult', extraAdults, rate.extraAdultPrice);
    // Children beyond the free allowance are charged, split by bed choice.
    const chargeableChildren = Math.max(0, children - baseChildren);
    const noBed = Math.min(childrenNoBed, chargeableChildren);
    const withBed = chargeableChildren - noBed;
    add('Child (with bed)', withBed, rate.childWithBedPrice);
    add('Child (no bed)', noBed, rate.childWithoutBedPrice);
  } else {
    const baseAdultsPriced = Math.min(adults, baseAdults);
    const extraAdults = Math.max(0, adults - baseAdults);
    add('Adult', baseAdultsPriced, rate.adultPrice);
    add('Extra adult', extraAdults, rate.extraAdultPrice);
    const withBed = children - childrenNoBed;
    add('Child (with bed)', withBed, rate.childWithBedPrice);
    add('Child (no bed)', childrenNoBed, rate.childWithoutBedPrice);
  }

  add('Infant', infants, rate.infantPrice);
  // Meals apply to adults + all children (regardless of bed).
  add('Meal (adult)', adults, rate.mealPerAdult);
  add('Meal (child)', children, rate.mealPerChild);

  const amount = lines.reduce((s, l) => s + l.amount, 0);
  return { amount, unavailableReason: null, lines, mode };
}

/**
 * Price one room across a stay. `nightlyRates` has one entry per night (each the
 * date-correct rate card). If any night is unavailable, the whole stay is
 * unavailable (skip-and-flag). Returns the summed amount + the first night's
 * breakdown for display.
 */
export function priceRoomStay(
  nightlyRates: HotelRateInput[],
  occ: RoomOccupancy,
): RoomNightPrice & { nights: number } {
  if (nightlyRates.length === 0) {
    return {
      amount: 0,
      unavailableReason: 'No rate for these dates',
      lines: [],
      mode: 'PER_PAX',
      nights: 0,
    };
  }
  let total = 0;
  let firstNight: RoomNightPrice | null = null;
  for (const rate of nightlyRates) {
    const night = priceRoomNight(rate, occ);
    if (night.unavailableReason) {
      return { ...night, nights: nightlyRates.length };
    }
    if (!firstNight) firstNight = night;
    total += night.amount;
  }
  return {
    amount: total,
    unavailableReason: null,
    lines: firstNight?.lines ?? [],
    mode: firstNight?.mode ?? 'PER_PAX',
    nights: nightlyRates.length,
  };
}
